export class RefundContractError extends Error {
    constructor(message) {
        super(message);
        this.name = "RefundContractError";
        this.kind = "contract";
    }
}

export const REFUND_REQUEST_STATUSES = Object.freeze([
    "requested",
    "approved",
    "rejected",
    "posted",
    "failed",
    "cancelled",
]);

export const REFUND_TYPES = Object.freeze(["full", "partial"]);

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const MONEY_PATTERN = /^(?:0|[1-9]\d*)\.\d{2}$/;
const ISO_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);

const object = (value, label) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
        throw new RefundContractError(`${label} must be an object`);
    }
    return value;
};

const field = (value, key, label = key) => {
    if (!hasOwn(value, key)) throw new RefundContractError(`${label} is required`);
    return value[key];
};

const text = (value, label) => {
    if (typeof value !== "string") throw new RefundContractError(`${label} must be text`);
    return value;
};

const nonEmptyText = (value, label) => {
    const result = text(value, label);
    if (!result.trim()) throw new RefundContractError(`${label} must not be empty`);
    return result;
};

const nullableText = (value, label) =>
    value === null ? null : text(value, label);

const uuid = (value, label) => {
    if (typeof value !== "string" || !UUID_PATTERN.test(value)) {
        throw new RefundContractError(`${label} must be a UUID`);
    }
    return value;
};

const nullableUuid = (value, label) =>
    value === null ? null : uuid(value, label);

const enumValue = (value, allowed, label) => {
    if (!allowed.includes(value)) {
        throw new RefundContractError(`${label} has an unknown value`);
    }
    return value;
};

const money = (value, label) => {
    if (typeof value !== "string" || !MONEY_PATTERN.test(value)) {
        throw new RefundContractError(`${label} must be a canonical money string`);
    }
    return value;
};

const nullableMoney = (value, label) =>
    value === null ? null : money(value, label);

const positiveInteger = (value, label) => {
    if (!Number.isInteger(value) || value <= 0) {
        throw new RefundContractError(`${label} must be a positive integer`);
    }
    return value;
};

const nullablePositiveInteger = (value, label) =>
    value === null ? null : positiveInteger(value, label);

const nonNegativeInteger = (value, label) => {
    if (!Number.isInteger(value) || value < 0) {
        throw new RefundContractError(`${label} must be a non-negative integer`);
    }
    return value;
};

const boolean = (value, label) => {
    if (value !== true && value !== false) {
        throw new RefundContractError(`${label} must be boolean`);
    }
    return value;
};

const timestamp = (value, label) => {
    if (
        typeof value !== "string" ||
        !ISO_TIMESTAMP_PATTERN.test(value) ||
        Number.isNaN(Date.parse(value))
    ) {
        throw new RefundContractError(`${label} must be an ISO timestamp`);
    }
    return value;
};

const nullableTimestamp = (value, label) =>
    value === null ? null : timestamp(value, label);

const pageValue = (value, label) => {
    if (value === null) return null;
    if (typeof value !== "string" || !value.trim()) {
        throw new RefundContractError(`${label} must be a page URL or null`);
    }

    let url;
    try {
        url = new URL(value, "https://refunds.invalid");
    } catch (_error) {
        throw new RefundContractError(`${label} must be a valid page URL`);
    }
    if (!url.pathname.endsWith("/api/v2/admin/refunds")) {
        throw new RefundContractError(`${label} must target posted refund history`);
    }
    const pages = url.searchParams.getAll("page");
    if (pages.length !== 1 || !/^[1-9]\d*$/.test(pages[0])) {
        throw new RefundContractError(`${label} must contain one positive page`);
    }
    return Number(pages[0]);
};

export const normalizeRefundRequest = (value) => {
    const request = object(value, "refund request");
    return {
        id: uuid(field(request, "id"), "id"),
        pay_bill_id: uuid(field(request, "pay_bill_id"), "pay_bill_id"),
        status: enumValue(field(request, "status"), REFUND_REQUEST_STATUSES, "status"),
        refund_type: enumValue(field(request, "refund_type"), REFUND_TYPES, "refund_type"),
        amount: nullableMoney(field(request, "amount"), "amount"),
        reason: text(field(request, "reason"), "reason"),
        idempotency_key: nonEmptyText(field(request, "idempotency_key"), "idempotency_key"),
        requester_id: positiveInteger(field(request, "requester_id"), "requester_id"),
        approver_id: nullablePositiveInteger(field(request, "approver_id"), "approver_id"),
        failure_code: text(field(request, "failure_code"), "failure_code"),
        posted_refund_id: nullableUuid(
            field(request, "posted_refund_id"),
            "posted_refund_id",
        ),
        requested_at: timestamp(field(request, "requested_at"), "requested_at"),
        decided_at: nullableTimestamp(field(request, "decided_at"), "decided_at"),
        posted_at: nullableTimestamp(field(request, "posted_at"), "posted_at"),
    };
};

export const normalizeRefundRequestList = (value) => {
    if (!Array.isArray(value)) throw new RefundContractError("refund requests must be an array");
    return value.map(normalizeRefundRequest);
};

export const normalizeRefundPolicy = (value) => {
    const policy = object(value, "refund policy");
    const refundWindowDays = field(policy, "refund_window_days");
    return {
        tenant_id: positiveInteger(field(policy, "tenant_id"), "tenant_id"),
        refund_window_days:
            refundWindowDays === null
                ? null
                : nonNegativeInteger(refundWindowDays, "refund_window_days"),
        requires_approval: boolean(field(policy, "requires_approval"), "requires_approval"),
        partial_refund_enabled: boolean(
            field(policy, "partial_refund_enabled"),
            "partial_refund_enabled",
        ),
        legacy_client_direct_enabled: boolean(
            field(policy, "legacy_client_direct_enabled"),
            "legacy_client_direct_enabled",
        ),
        version: positiveInteger(field(policy, "version"), "version"),
    };
};

export const normalizePostedRefund = (value) => {
    const refund = object(value, "posted refund");
    return {
        id: uuid(field(refund, "id"), "id"),
        pay_bill_id: uuid(field(refund, "pay_bill_id"), "pay_bill_id"),
        refund_type: enumValue(field(refund, "refund_type"), REFUND_TYPES, "refund_type"),
        status: enumValue(field(refund, "status"), ["posted"], "status"),
        amount: money(field(refund, "amount"), "amount"),
        main_amount: money(field(refund, "main_amount"), "main_amount"),
        points_amount: money(field(refund, "points_amount"), "points_amount"),
        cashup_amount: money(field(refund, "cashup_amount"), "cashup_amount"),
        reason: text(field(refund, "reason"), "reason"),
        created_by_id: nullablePositiveInteger(
            field(refund, "created_by_id"),
            "created_by_id",
        ),
        created_by_name: nullableText(field(refund, "created_by_name"), "created_by_name"),
        created_at: timestamp(field(refund, "created_at"), "created_at"),
    };
};

export const normalizePostedRefundPage = (value) => {
    const page = object(value, "posted refund page");
    const results = field(page, "results");
    if (!Array.isArray(results)) throw new RefundContractError("results must be an array");
    return {
        count: nonNegativeInteger(field(page, "count"), "count"),
        next: pageValue(field(page, "next"), "next"),
        previous: pageValue(field(page, "previous"), "previous"),
        results: results.map(normalizePostedRefund),
    };
};
